import { MAX_FRAME_DT, TICK_DT } from "../config.ts";

/**
 * Fixed-timestep game loop with an accumulator and render interpolation.
 * The simulation always advances in discrete TICK_DT steps (deterministic),
 * while rendering can happen at any display rate. `alpha` is the fractional
 * progress toward the next sim step, used to interpolate visuals smoothly.
 *
 * A `timeScale` lets us slow the *simulation* for hit-stop juice without
 * affecting the realtime render cadence.
 */
export class GameLoop {
  private accumulator = 0;
  private last = 0;
  private raf = 0;
  private running = false;

  timeScale = 1;

  constructor(
    private readonly step: (dt: number) => void,
    private readonly render: (alpha: number, frameDt: number) => void,
  ) {}

  start(): void {
    if (this.running) return;
    this.running = true;
    this.last = performance.now();
    this.accumulator = 0;
    this.raf = requestAnimationFrame(this.frame);
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.raf);
  }

  private frame = (now: number): void => {
    if (!this.running) return;
    this.raf = requestAnimationFrame(this.frame);

    let frameDt = (now - this.last) / 1000;
    this.last = now;
    if (frameDt > MAX_FRAME_DT) frameDt = MAX_FRAME_DT; // avoid spiral of death

    this.accumulator += frameDt * this.timeScale;
    while (this.accumulator >= TICK_DT) {
      this.step(TICK_DT);
      this.accumulator -= TICK_DT;
    }

    this.render(this.accumulator / TICK_DT, frameDt);
  };
}
